import { useMemo, useState, useCallback } from 'react'

import { isDisplayable } from '../utils/productUtils.js'

/**
 * Shared mobile preview carousel state for SVG-driven product previews.
 * Keeps TiersView focused on orchestration (data + rendering) instead of
 * gesture/index bookkeeping.
 */
export function useMobilePreviewCarousel({
  products,
  filteredProducts,
  effectiveBuilding,
  activeTier,
  previewProduct,
  isSheetMinimized,
  carouselCandidates = null,
  hasActiveItem = null,
}) {
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [swipeActionSide, setSwipeActionSide] = useState(null)

  const visibleProductsByUuid = useMemo(
    () => new Set(filteredProducts.map(product => product.uuid)),
    [filteredProducts],
  )

  const tierProducts = useMemo(() => {
    if (Array.isArray(carouselCandidates)) return carouselCandidates

    if (!effectiveBuilding || activeTier == null) return []

    return products
      .filter(product =>
        product.building_id === effectiveBuilding
        && product.ref_tier == activeTier
        && isDisplayable(product.visibility),
      )
      .sort((a, b) => {
        const aId = String(a.id_original ?? a.id_cad ?? '')
        const bId = String(b.id_original ?? b.id_cad ?? '')
        return aId.localeCompare(bId)
      })
  }, [products, effectiveBuilding, activeTier, carouselCandidates])

  const previewIndex = useMemo(() => {
    const previewUuid = previewProduct?.uuid
    if (!previewUuid) return -1
    return tierProducts.findIndex(product => product.uuid === previewUuid)
  }, [tierProducts, previewProduct?.uuid])

  const onSwipeMove = useCallback((dx) => {
    if (!(previewProduct ?? hasActiveItem) || isSheetMinimized) return

    const clamped = Math.max(-96, Math.min(96, dx))
    setSwipeOffset(clamped)

    if (Math.abs(clamped) >= 12) {
      setSwipeActionSide(clamped > 0 ? 'left' : 'right')
    } else {
      setSwipeActionSide(null)
    }
  }, [previewProduct, hasActiveItem, isSheetMinimized])

  const markSwipePrev = useCallback(() => {
    setSwipeActionSide('left')
    setSwipeOffset(0)
  }, [])

  const markSwipeNext = useCallback(() => {
    setSwipeActionSide('right')
    setSwipeOffset(0)
  }, [])

  const resetSwipeUi = useCallback(() => {
    setSwipeOffset(0)
    setSwipeActionSide(null)
  }, [])

  return {
    visibleProductsByUuid,
    tierProducts,
    previewIndex,
    swipeOffset,
    swipeActionSide,
    onSwipeMove,
    markSwipePrev,
    markSwipeNext,
    resetSwipeUi,
  }
}
